import {useGetExtensionsQuery} from "./queries.generated";
import RelatedDataResolver, {ReferenceType, RelatedDataTargetType} from "@/components/RelatedDataResolver";
import {useEffect, useMemo} from "react";
import ErrorHelper from "@/utils/ErrorHelper";
import {ReferenceRetargetsUser} from "@/api";
import {getTargetsForReferenceType, isResolved, ProcessedUserRelatedData} from "@/utils/relatedDataTools";

type Props = {
    domainId: number;
    data: ReferenceRetargetsUser;
    references: ProcessedUserRelatedData;
    resolvedCallback: (resolved: boolean) => void;
    resolutionCallback: (resolution: ReferenceRetargetsUser) => void;
    errorHelper: ErrorHelper;
};

const ExtensionRelatedData = ({
    domainId,
    data,
    references,
    resolvedCallback,
    resolutionCallback,
    errorHelper,
}: Props) => {
    const {data: targetsData, loading: targetsLoading} = useGetExtensionsQuery({
        variables: {
            domainId,
        },
        fetchPolicy: "no-cache",
    });

    // targets
    const {phoneButtonsTargets, ivrActionTargets, gatewayTargets, overrideCallerTargets} = useMemo(
        () => ({
            phoneButtonsTargets: getTargetsForReferenceType(ReferenceType.PHONE_BUTTON, targetsData),
            ivrActionTargets: getTargetsForReferenceType(ReferenceType.IVR_ACTION, targetsData),
            gatewayTargets: getTargetsForReferenceType(ReferenceType.GATEWAY, targetsData),
            overrideCallerTargets: getTargetsForReferenceType(ReferenceType.OVERRIDE_CALLER, targetsData),
        }),
        [targetsData],
    );

    useEffect(() => {
        resolvedCallback(isResolved(data));
    }, [data, resolvedCallback]);

    if (targetsLoading) {
        return null;
    }

    function onChange(resolution: ReferenceRetargetsUser) {
        resolutionCallback(resolution);
    }

    return (
        <RelatedDataResolver<RelatedDataTargetType.EXTENSION, ReferenceRetargetsUser>
            domainId={domainId}
            onChange={onChange}
            errorHelper={errorHelper}
            value={data}
            phoneButtons={{
                references: references?.phoneButtons,
                targets: phoneButtonsTargets,
            }}
            ivrActions={{
                references: references?.ivrActions,
                targets: ivrActionTargets,
            }}
            gateways={{
                references: references?.gateways,
                targets: gatewayTargets,
            }}
            secretary={{
                references: references?.secretary,
                targets: phoneButtonsTargets,
            }}
            overrideCaller={{
                references: references?.overrideCaller,
                targets: overrideCallerTargets,
            }}
        />
    );
};

export default ExtensionRelatedData;
